/**
 * @file ConfirmModal.jsx
 * @location src/components/ui/sharedui/ConfirmModal.jsx
 * @role 삭제/초기화 확인 모달 (소형) — ESC 닫기 + focus trap + role="dialog"
 *
 * Overlay 의존성을 피해 ui 레이어 자체 wrapper 사용 (sharedui → modals 역 import 방지).
 * 동일한 a11y 보장: ESC, Tab/Shift+Tab 순환, 캔슬 버튼 초기 포커스, 해제 시 이전 포커스 복귀.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon.jsx';

const FOCUSABLE = [
  'button:not([disabled])', 'a[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * @param {object} props
 * @param {string} props.msg
 * @param {string} [props.subMsg]
 * @param {React.ReactNode} [props.children]  msg·subMsg 아래 커스텀 콘텐츠 (Before→After 미리보기 등)
 * @param {() => void} props.onOk
 * @param {() => void} props.onCancel
 * @param {string} [props.okLabel]
 * @param {string} [props.okClass]  OK 버튼 className (기본 'btns del'=위험. 완료확인 등 비-danger 는 'btnp')
 */
export function ConfirmModal({ msg, subMsg, children, onOk, onCancel, okLabel = '삭제', okClass = 'btns del' }) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const prevActive = document.activeElement;

    // 캔슬 버튼에 초기 포커스 — 위험 행동 회피
    const list = Array.from(node.querySelectorAll(FOCUSABLE));
    const cancelBtn = list.find(el => el.textContent?.includes('취소')) ?? list[0];
    cancelBtn?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onCancel(); return; }
      if (e.key !== 'Tab' || list.length === 0) return;
      const first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { last.focus();  e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
    };
    node.addEventListener('keydown', onKey);
    return () => {
      node.removeEventListener('keydown', onKey);
      if (prevActive && typeof prevActive.focus === 'function') prevActive.focus();
    };
  }, [onCancel]);

  const body = (
    <div
      ref={ref}
      className="moverlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      onClick={e => e.target === e.currentTarget && onCancel()}
    >
      <div className="modal modal-sm"
        style={{ animation: 'modal-pop .18s cubic-bezier(.22,.7,.36,1)' }}>
        <div className="mhd">
          <div className="mtit" id="confirm-title"><Icon name="warn" size={18} /> 확인</div>
          <button className="mclose" onClick={onCancel} aria-label="닫기"><Icon name="x" size={16} /></button>
        </div>
        <div className="mbody">
          <p style={{ fontSize: 14, color: 'var(--t1)', lineHeight: 1.75, margin: 0 }}>{msg}</p>
          {subMsg && <p style={{ fontSize: 13, color: 'var(--t3)', marginTop: 4 }}>{subMsg}</p>}
          {children}
          <div className="mfoot" style={{ marginTop: 8 }}>
            <button className="btns" onClick={onCancel}>취소</button>
            <button className={okClass} onClick={onOk}>{okLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );

  // SSR-safe portal — document 없으면 inline (테스트/SSR 환경)
  return typeof document === 'undefined' ? body : createPortal(body, document.body);
}
